import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { JobVacancyPhaseTypeEnumIcons, JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { JobCandidate, JobCandidatePhaseProgress } from 'src/app/models/job_candidate.model';
import { JobVacancyPhase } from 'src/app/models/job_vacancy.model';

export type JobCandidatePhaseStepState = 'pending' | 'current' | 'completed' | 'discarded';

export interface JobCandidatePhaseTrackItem {
  phase_id: number;
  name: string;
  type_label: string;
  icon: string;
  state: JobCandidatePhaseStepState;
  status_label: string;
  tooltip: string;
}

@Component({
  selector: 'app-job-vacancy-candidate-phase-trackline',
  templateUrl: './job-vacancy-candidate-phase-trackline.component.html',
  styleUrl: './job-vacancy-candidate-phase-trackline.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule],
})
export class JobCandidatePhaseTracklineComponent {
  private readonly date_pipe = inject(DatePipe);

  readonly phases = input<JobVacancyPhase[]>([]);
  readonly job_candidate = input<JobCandidate | null>(null);

  readonly phase_track_items = computed((): JobCandidatePhaseTrackItem[] => {
    const job_candidate = this.job_candidate();
    const sorted_phases = [...this.phases()].sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order);

    if (!job_candidate) {
      return [];
    }

    const current_phase_id = job_candidate.active_job_vacancy_candidate_phase_progress?.job_vacancy_phase_id ?? null;

    return sorted_phases.map(job_vacancy_phase => {
      const phase_progress_list =
        job_candidate.job_vacancy_candidate_phase_progress?.filter(progress => progress.job_vacancy_phase_id === job_vacancy_phase.id) ??
        [];

      const latest_progress = [...phase_progress_list].sort((first_progress, second_progress) =>
        second_progress.entered_at.localeCompare(first_progress.entered_at)
      )[0];

      const state = this.resolvePhaseStepState(latest_progress, current_phase_id, job_vacancy_phase.id);
      const type_label = JobVacancyPhaseTypeEnumLabels[job_vacancy_phase.type] ?? job_vacancy_phase.type;

      return {
        phase_id: job_vacancy_phase.id,
        name: job_vacancy_phase.name,
        type_label,
        icon: JobVacancyPhaseTypeEnumIcons[job_vacancy_phase.type] ?? 'circle',
        state,
        status_label: this.buildStatusLabel(state, latest_progress),
        tooltip: this.buildTooltip(job_vacancy_phase, type_label, state, latest_progress),
      };
    });
  });

  private resolvePhaseStepState(
    latest_progress: JobCandidatePhaseProgress | undefined,
    current_phase_id: number | null,
    job_vacancy_phase_id: number
  ): JobCandidatePhaseStepState {
    if (!latest_progress) {
      return 'pending';
    }

    if (latest_progress.discarded_at !== null) {
      return 'discarded';
    }

    const is_open_progress = latest_progress.completed_at === null && latest_progress.discarded_at === null;

    if (is_open_progress && current_phase_id === job_vacancy_phase_id) {
      return 'current';
    }

    if (latest_progress.completed_at !== null) {
      return 'completed';
    }

    if (is_open_progress) {
      return 'current';
    }

    return 'pending';
  }

  private buildStatusLabel(state: JobCandidatePhaseStepState, latest_progress: JobCandidatePhaseProgress | undefined): string {
    switch (state) {
      case 'current':
        return 'En curso';
      case 'completed':
        return latest_progress?.completed_at ? `Completada ${this.formatDate(latest_progress.completed_at)}` : 'Completada';
      case 'discarded':
        return latest_progress?.discarded_at ? `Descartada ${this.formatDate(latest_progress.discarded_at)}` : 'Descartada';
      default:
        return 'Pendiente';
    }
  }

  private buildTooltip(
    job_vacancy_phase: JobVacancyPhase,
    type_label: string,
    state: JobCandidatePhaseStepState,
    latest_progress: JobCandidatePhaseProgress | undefined
  ): string {
    const lines = [`${job_vacancy_phase.name} (${type_label})`, `Estado: ${this.buildStatusLabel(state, latest_progress)}`];

    if (latest_progress) {
      lines.push(`Entrada: ${this.formatDateTime(latest_progress.entered_at)}`);

      if (latest_progress.completed_at) {
        lines.push(`Completada: ${this.formatDateTime(latest_progress.completed_at)}`);
      }

      if (latest_progress.discarded_at) {
        lines.push(`Descartada: ${this.formatDateTime(latest_progress.discarded_at)}`);
      }
    }

    return lines.join('\n');
  }

  private formatDate(value: string): string {
    return this.date_pipe.transform(value, 'dd/MM/yyyy') ?? value;
  }

  private formatDateTime(value: string): string {
    return this.date_pipe.transform(value, 'dd/MM/yyyy HH:mm') ?? value;
  }
}
